import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  List,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  ListItemIcon,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Badge,
  styled,
  alpha,
  Tooltip,
  Button,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Collapse,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Hub as GraphIcon,
  Tag as TagIcon,
  SmartToy as SmartToyIcon,
  ExpandLess,
  ExpandMore,
  Explore as ExploreIcon,
  PeopleAlt as PeopleAltIcon,
  PersonRemove as PersonRemoveIcon,
} from '@mui/icons-material';
import Avatar from '@mui/material/Avatar';
import { formatDistanceToNow } from 'date-fns';
import { Conversation, Agent, useChatStore } from '../../stores/chatStore';
import { ConversationService } from '../../services/api/conversation';
import { knowledgeApi } from '../../services/api/knowledge.service';
import AgentAvatar from './AgentAvatar';
import TopicNavigation from './TopicNavigation';
import PresenceIndicator from './PresenceIndicator';
import ChannelBrowser from './ChannelBrowser';
import CreateConversationDialog from './CreateConversationDialog';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { messagingService, Participant } from '../../services/api/messaging.service';
import UserSearchPicker, { OrgUser } from './UserSearchPicker';

interface ChatSidebarProps {
  conversations: Conversation[];
  agents: Agent[];
  currentConversation: Conversation | null;
  onSelectConversation: (conversation: Conversation) => void;
  onCreateConversation: () => void;
  onConversationUpdate?: (conversation: Conversation) => void;
  onConversationDelete?: (conversationId: string) => void;
  onlineUsers?: string[];
  selectedTopics?: string[];
  onTopicSelect?: (topicId: string) => void;
  onTopicDeselect?: (topicId: string) => void;
  onClearTopicFilters?: () => void;
}

const SidebarContainer = styled('div')(({ theme }) => ({
  width: '100%',
  height: '100%',
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}));

const SidebarHeader = styled('div')(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const SearchField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: alpha(theme.palette.action.hover, 0.04),
  },
}));

const ConversationItem = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'hasUnread',
})<{ selected: boolean; hasUnread?: boolean }>(({ theme, selected, hasUnread }) => ({
  borderRadius: theme.shape.borderRadius,
  margin: theme.spacing(0.25, 1),
  padding: theme.spacing(0.5, 1),
  backgroundColor: selected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
  '&:hover': {
    backgroundColor: selected
      ? alpha(theme.palette.primary.main, 0.12)
      : alpha(theme.palette.action.hover, 0.04),
  },
  ...(hasUnread && !selected && {
    '& .MuiListItemText-primary': {
      fontWeight: 700,
    },
  }),
}));

const SectionHeader = styled(ListItemButton)(({ theme }) => ({
  padding: theme.spacing(0.5, 2),
  minHeight: 32,
  '&:hover': {
    backgroundColor: alpha(theme.palette.action.hover, 0.04),
  },
}));

const AgentSection = styled('div')(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  flexShrink: 0,
  maxHeight: '200px',
  overflowY: 'auto',
}));

const OnlineIndicator = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#44b700',
    color: '#44b700',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: 'ripple 1.2s infinite ease-in-out',
      border: '1px solid currentColor',
      content: '""',
    },
  },
  '@keyframes ripple': {
    '0%': { transform: 'scale(.8)', opacity: 1 },
    '100%': { transform: 'scale(2.4)', opacity: 0 },
  },
}));

const ChatSidebar: React.FC<ChatSidebarProps> = ({
  conversations = [],
  agents = [],
  currentConversation,
  onSelectConversation,
  onCreateConversation,
  onConversationUpdate,
  onConversationDelete,
  onlineUsers = [],
  selectedTopics = [],
  onTopicSelect,
  onTopicDeselect,
  onClearTopicFilters,
}) => {
  const navigate = useNavigate();
  const flags = useFeatureFlags();
  const [searchTerm, setSearchTerm] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<Set<string>>(new Set());
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const conversationService = new ConversationService();

  // Collapsible sections state
  const [sectionsOpen, setSectionsOpen] = useState({
    conversations: true,
    channels: true,
    teammates: true,
  });

  // Channel browser and create dialog
  const [channelBrowserOpen, setChannelBrowserOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // People management dialog
  const [peopleDialogOpen, setPeopleDialogOpen] = useState(false);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);

  const unreadCounts = useChatStore((s) => s.unreadCounts);
  const markRead = useChatStore((s) => s.markConversationRead);

  // Perform semantic search when user types
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!searchTerm.trim()) {
      setSearchResults(new Set());
      setSearching(false);
      return;
    }

    setSearching(true);

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const activeAgent = agents.find(a => a.is_active);
        if (!activeAgent) {
          setSearching(false);
          return;
        }

        const response = await knowledgeApi.semanticSearch(
          activeAgent.id,
          searchTerm,
          { nodeTypes: ['Message'], limit: 50, minScore: 0.3 }
        );

        const conversationIds = new Set<string>();
        response.results.forEach((result: any) => {
          if (result.conversationId) conversationIds.add(result.conversationId);
          else if (result['Message.conversationId']) conversationIds.add(result['Message.conversationId']);
        });

        setSearchResults(conversationIds);
      } catch (error) {
        console.error('[ChatSidebar] Semantic search failed:', error);
        setSearchResults(new Set());
      } finally {
        setSearching(false);
      }
    }, 500);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchTerm, agents]);

  // Filter conversations
  const filteredConversations = conversations.filter((conv) => {
    const titleMatch = conv.title?.toLowerCase().includes(searchTerm.toLowerCase());
    const contentMatch = searchResults.has(conv.id);
    return titleMatch || contentMatch;
  });

  // Categorize: everything that's not a channel goes in "Conversations"
  const nonChannelConversations = filteredConversations.filter(c => c.conversation_type !== 'channel');
  const channels = filteredConversations.filter(c => c.conversation_type === 'channel');

  const activeAgents = agents.filter((agent) => agent.is_active);

  const formatLastMessage = (date: Date | string | undefined) => {
    if (!date) return '';
    return formatDistanceToNow(new Date(date), { addSuffix: true });
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, conversation: Conversation) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedConversation(conversation);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleRenameClick = () => {
    if (selectedConversation) {
      setNewTitle(selectedConversation.title || 'Untitled Conversation');
      setRenameDialogOpen(true);
      handleMenuClose();
    }
  };

  const handleRenameConfirm = async () => {
    if (selectedConversation && newTitle.trim()) {
      try {
        await conversationService.updateConversation(selectedConversation.id, { title: newTitle.trim() });
        if (onConversationUpdate) {
          onConversationUpdate({ ...selectedConversation, title: newTitle.trim() });
        }
        setRenameDialogOpen(false);
        setNewTitle('');
        setSelectedConversation(null);
      } catch (error) {
        console.error('Failed to rename conversation:', error);
      }
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handlePeopleClick = () => {
    setPeopleDialogOpen(true);
    handleMenuClose();
    if (selectedConversation) {
      loadParticipants(selectedConversation.id);
    }
  };

  const loadParticipants = async (conversationId: string) => {
    setLoadingParticipants(true);
    try {
      const result = await messagingService.getParticipants(conversationId);
      setParticipants(result);
    } catch (error) {
      console.error('Failed to load participants:', error);
      setParticipants([]);
    } finally {
      setLoadingParticipants(false);
    }
  };

  const handleInviteUsers = async (users: OrgUser[]) => {
    if (!selectedConversation || users.length === 0) return;
    try {
      await messagingService.addParticipants(selectedConversation.id, users.map(u => u.id));
      loadParticipants(selectedConversation.id);
      // Reload conversations to pick up type changes (ai_chat -> group)
      useChatStore.getState().loadConversations();
    } catch (error) {
      console.error('Failed to invite participants:', error);
    }
  };

  const handleRemoveParticipant = async (userId: string) => {
    if (!selectedConversation) return;
    try {
      await messagingService.removeParticipant(selectedConversation.id, userId);
      setParticipants(prev => prev.filter(p => p.user_id !== userId));
    } catch (error) {
      console.error('Failed to remove participant:', error);
    }
  };

  const getParticipantName = (p: Participant) => {
    if (p.first_name || p.last_name) {
      return `${p.first_name || ''} ${p.last_name || ''}`.trim();
    }
    return p.email || 'Unknown';
  };

  const getParticipantInitials = (p: Participant) => {
    const name = getParticipantName(p);
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleDeleteConfirm = async () => {
    if (selectedConversation) {
      try {
        await conversationService.deleteConversation(selectedConversation.id);
        if (onConversationDelete) onConversationDelete(selectedConversation.id);
        setDeleteDialogOpen(false);
        setSelectedConversation(null);
      } catch (error) {
        console.error('Failed to delete conversation:', error);
      }
    }
  };

  const handleSelectConversation = (conversation: Conversation) => {
    onSelectConversation(conversation);
    // Mark as read
    if (unreadCounts.has(conversation.id)) {
      markRead(conversation.id);
    }
  };

  const handleCreateConversation = async (data: any) => {
    try {
      if (data.type === 'ai_chat') {
        onCreateConversation();
        return;
      } else if (data.type === 'group') {
        await messagingService.createGroupChat(data.title || 'New Conversation', data.participantIds || [], data.historyVisible);
      } else if (data.type === 'channel') {
        await messagingService.createChannel(data.title, data.slug, data.description, data.isPublic);
      }
      // Reload conversations
      useChatStore.getState().loadConversations();
    } catch (error) {
      console.error('Failed to create conversation:', error);
    }
  };

  const toggleSection = (section: keyof typeof sectionsOpen) => {
    setSectionsOpen(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getTypeIcon = (conversation: Conversation) => {
    if (conversation.conversation_type === 'channel') {
      return <TagIcon fontSize="small" style={{ color: '#999' }} />;
    }
    if (conversation.participant_count && Number(conversation.participant_count) > 1) {
      return <GroupIcon fontSize="small" style={{ color: '#999' }} />;
    }
    return <SmartToyIcon fontSize="small" style={{ color: '#4DC4D2' }} />;
  };

  const renderConversationItem = (conversation: Conversation) => {
    const unread = unreadCounts.get(conversation.id);
    const isSelected = currentConversation?.id === conversation.id;

    return (
      <ConversationItem
        key={conversation.id}
        selected={isSelected}
        hasUnread={!!unread && unread.count > 0}
        onClick={() => handleSelectConversation(conversation)}
      >
        <ListItemIcon style={{ minWidth: 32 }}>
          {getTypeIcon(conversation)}
        </ListItemIcon>
        <ListItemText
          primary={
            conversation.conversation_type === 'channel'
              ? `#${conversation.slug || conversation.title}`
              : conversation.title || 'Untitled Conversation'
          }
          secondary={formatLastMessage(conversation.last_message_at || conversation.updated_at)}
          primaryTypographyProps={{
            noWrap: true,
            variant: 'body2',
            fontWeight: (unread && unread.count > 0 && !isSelected) ? 700 : isSelected ? 600 : 400,
          }}
          secondaryTypographyProps={{ noWrap: true, variant: 'caption' }}
        />
        {unread && unread.count > 0 && !isSelected && (
          <Badge
            badgeContent={unread.count}
            color={unread.hasMention ? 'error' : 'primary'}
            style={{ marginRight: 8 }}
          />
        )}
        <Tooltip title="People">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedConversation(conversation);
              setPeopleDialogOpen(true);
              loadParticipants(conversation.id);
            }}
          >
            <PeopleAltIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <IconButton
          size="small"
          onClick={(e) => handleMenuOpen(e, conversation)}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </ConversationItem>
    );
  };

  const renderSectionedLayout = () => (
    <List style={{ flex: 1, overflowY: 'auto', paddingTop: 0, paddingBottom: 0 }}>
      {/* Conversations section */}
      {nonChannelConversations.length > 0 && (
        <React.Fragment key="section-conversations">
          <SectionHeader onClick={() => toggleSection('conversations')}>
            <ListItemText
              primary="Conversations"
              primaryTypographyProps={{ variant: 'caption', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}
            />
            {sectionsOpen.conversations ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </SectionHeader>
          <Collapse in={sectionsOpen.conversations}>
            {nonChannelConversations.map(renderConversationItem)}
          </Collapse>
        </React.Fragment>
      )}

      {/* Channels section */}
      <SectionHeader onClick={() => toggleSection('channels')}>
        <ListItemText
          primary="Channels"
          primaryTypographyProps={{ variant: 'caption', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}
        />
        <Tooltip title="Browse channels">
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); setChannelBrowserOpen(true); }}
            style={{ marginRight: 4 }}
          >
            <ExploreIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {sectionsOpen.channels ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
      </SectionHeader>
      <Collapse in={sectionsOpen.channels}>
        {channels.length > 0 ? (
          channels.map(renderConversationItem)
        ) : (
          <div style={{ paddingLeft: 16, paddingRight: 16, paddingTop: 8, paddingBottom: 8 }}>
            <Button
              size="small"
              variant="text"
              onClick={() => setChannelBrowserOpen(true)}
              startIcon={<ExploreIcon fontSize="small" />}
            >
              Browse channels
            </Button>
          </div>
        )}
      </Collapse>

      {/* Empty state */}
      {filteredConversations.length === 0 && (
        <div style={{ textAlign: 'center', paddingTop: 32, paddingBottom: 32 }}>
          <Typography variant="body2" color="text.secondary">
            {searching ? 'Searching...' : searchTerm ? 'No conversations found' : 'No conversations yet'}
          </Typography>
          {!searchTerm && !searching && (
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)} style={{ marginTop: 16 }}>
              Start a conversation
            </Button>
          )}
        </div>
      )}
    </List>
  );

  const renderFlatLayout = () => (
    <List style={{ flex: 1, overflowY: 'auto', paddingTop: 0, paddingBottom: 0 }}>
      {filteredConversations.length === 0 ? (
        <div style={{ textAlign: 'center', paddingTop: 32, paddingBottom: 32 }}>
          <Typography variant="body2" color="text.secondary">
            {searching ? 'Searching...' : searchTerm ? 'No conversations or messages found' : 'No conversations yet'}
          </Typography>
          {!searchTerm && !searching && (
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={onCreateConversation} style={{ marginTop: 16 }}>
              Start a conversation
            </Button>
          )}
        </div>
      ) : (
        filteredConversations.map((conversation) => (
          <ConversationItem
            key={conversation.id}
            selected={currentConversation?.id === conversation.id}
            onClick={() => onSelectConversation(conversation)}
          >
            <ListItemAvatar>
              <GroupIcon />
            </ListItemAvatar>
            <ListItemText
              primary={conversation.title || 'Untitled Conversation'}
              secondary={formatLastMessage(conversation.last_message_at || conversation.updated_at)}
              primaryTypographyProps={{
                noWrap: true,
                fontWeight: currentConversation?.id === conversation.id ? 600 : 400,
              }}
              secondaryTypographyProps={{ noWrap: true }}
            />
            {conversation.message_count && conversation.message_count > 0 && (
              <Badge badgeContent={conversation.message_count} color="primary" style={{ marginRight: 8 }} />
            )}
            <IconButton
              size="small"
              onClick={(e) => handleMenuOpen(e, conversation)}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </ConversationItem>
        ))
      )}
    </List>
  );

  return (
    <SidebarContainer>
      <SidebarHeader>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <Typography variant="h6" fontWeight={600}>
            Conversations
          </Typography>
          <div style={{ display: 'flex', gap: 4 }}>
            <Tooltip title="Knowledge Graph (BETA) - Visualize your conversations">
              <IconButton
                onClick={() => navigate('/knowledge-graph')}
                color="secondary"
              >
                <GraphIcon />
              </IconButton>
            </Tooltip>
            {flags.slackMessaging ? (
              <Tooltip title="New conversation">
                <IconButton onClick={() => setCreateDialogOpen(true)} color="primary" size="small">
                  <AddIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="New AI chat">
                <IconButton onClick={onCreateConversation} color="primary">
                  <AddIcon />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </div>

        <SearchField
          fullWidth
          size="small"
          placeholder="Search conversations and messages..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {searching ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" />}
              </InputAdornment>
            ),
          }}
        />
      </SidebarHeader>

      {/* Render sectioned or flat layout based on feature flag */}
      {flags.slackMessaging ? renderSectionedLayout() : renderFlatLayout()}

      {/* Topic Navigation */}
      {onTopicSelect && onTopicDeselect && onClearTopicFilters && (
        <TopicNavigation
          selectedTopics={selectedTopics}
          onTopicSelect={onTopicSelect}
          onTopicDeselect={onTopicDeselect}
          onClearAll={onClearTopicFilters}
        />
      )}

      <AgentSection>
        <div>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {flags.slackMessaging ? 'Teammates' : 'Active Teammates'} ({activeAgents.length})
          </Typography>
          {activeAgents.length === 0 ? (
            <div style={{ paddingTop: 16, paddingBottom: 16, textAlign: 'center', color: 'rgba(0,0,0,0.6)', fontSize: '0.875rem' }}>
              <Typography variant="body2" color="warning.main" gutterBottom>
                No teammates available
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Your session may have expired. Try logging out and back in.
              </Typography>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {activeAgents.map((agent, index) => (
                <Tooltip key={`${agent.id}-${index}`} title={`${agent.custom_name || agent.name} (${agent.type})`}>
                  <div style={{ position: 'relative' }}>
                    {agent.status && agent.status !== 'idle' ? (
                      <OnlineIndicator overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} variant="dot">
                        <AgentAvatar agent={agent} size={36} />
                      </OnlineIndicator>
                    ) : (
                      <AgentAvatar agent={agent} size={36} />
                    )}
                  </div>
                </Tooltip>
              ))}
            </div>
          )}
          {onlineUsers.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Online Users ({onlineUsers.length})
              </Typography>
              <div style={{ display: 'flex', gap: 8 }}>
                {onlineUsers.slice(0, 5).map((userId) => (
                  <Tooltip key={userId} title={`User ${userId}`}>
                    <div style={{ position: 'relative' }}>
                      <Avatar style={{ width: 32, height: 32 }}>
                        <PersonIcon fontSize="small" />
                      </Avatar>
                      <div style={{ position: 'absolute', bottom: -1, right: -1 }}>
                        <PresenceIndicator isOnline={true} size={8} />
                      </div>
                    </div>
                  </Tooltip>
                ))}
                {onlineUsers.length > 5 && (
                  <Avatar style={{ width: 32, height: 32, fontSize: '0.75rem' }}>
                    +{onlineUsers.length - 5}
                  </Avatar>
                )}
              </div>
            </div>
          )}
        </div>
      </AgentSection>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handlePeopleClick}>
          <PeopleAltIcon fontSize="small" style={{ marginRight: 8 }} />
          People
        </MenuItem>
        <MenuItem onClick={handleRenameClick}>
          <EditIcon fontSize="small" style={{ marginRight: 8 }} />
          Rename
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} style={{ color: '#d32f2f' }}>
          <DeleteIcon fontSize="small" style={{ marginRight: 8 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onClose={() => { setRenameDialogOpen(false); setNewTitle(''); setSelectedConversation(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Rename Conversation</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Conversation Name"
            fullWidth
            variant="outlined"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyPress={(e) => { if (e.key === 'Enter') handleRenameConfirm(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRenameDialogOpen(false); setNewTitle(''); setSelectedConversation(null); }}>Cancel</Button>
          <Button onClick={handleRenameConfirm} variant="contained" disabled={!newTitle.trim()}>Rename</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => { setDeleteDialogOpen(false); setSelectedConversation(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Conversation</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the conversation "{selectedConversation?.title || 'Untitled Conversation'}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteDialogOpen(false); setSelectedConversation(null); }}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* People Management Dialog */}
      <Dialog
        open={peopleDialogOpen}
        onClose={() => { setPeopleDialogOpen(false); setParticipants([]); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          People — {selectedConversation?.title || 'Conversation'}
        </DialogTitle>
        <DialogContent>
          <div style={{ marginBottom: 16 }}>
            <Typography variant="caption" color="text.secondary" style={{ marginBottom: 8, display: 'block' }}>
              Invite members
            </Typography>
            <UserSearchPicker
              onSelectUsers={handleInviteUsers}
              excludeUserIds={participants.map(p => p.user_id)}
              label="Search people"
              placeholder="Name or email..."
            />
            <Typography variant="caption" color="text.secondary" style={{ marginTop: 4, display: 'block' }}>
              AI teammates are always available in every conversation.
            </Typography>
          </div>

          <Typography variant="subtitle2" color="text.secondary" style={{ marginBottom: 8 }}>
            Members ({participants.length})
          </Typography>

          {loadingParticipants ? (
            <div style={{ textAlign: 'center', paddingTop: 16, paddingBottom: 16 }}>
              <CircularProgress size={24} />
            </div>
          ) : participants.length === 0 ? (
            <Typography variant="body2" color="text.secondary" style={{ paddingTop: 16, paddingBottom: 16, textAlign: 'center' }}>
              Just you in this conversation. Invite someone above.
            </Typography>
          ) : (
            <List dense style={{ marginLeft: -16, marginRight: -16 }}>
              {participants.map((p) => (
                <ListItemButton key={p.id} style={{ paddingTop: 4, paddingBottom: 4, cursor: 'default' }} disableRipple>
                  <ListItemAvatar style={{ minWidth: 40 }}>
                    <Avatar style={{ width: 32, height: 32, fontSize: 14 }}>
                      {getParticipantInitials(p)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Typography variant="body2" noWrap>
                          {getParticipantName(p)}
                        </Typography>
                        {p.role === 'creator' && (
                          <Typography variant="caption" color="text.secondary">(creator)</Typography>
                        )}
                      </div>
                    }
                    secondary={p.email}
                    secondaryTypographyProps={{ noWrap: true, variant: 'caption' }}
                  />
                  {p.role !== 'creator' && (
                    <Tooltip title="Remove">
                      <IconButton size="small" onClick={() => handleRemoveParticipant(p.user_id)}>
                        <PersonRemoveIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPeopleDialogOpen(false); setParticipants([]); }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Channel Browser Dialog */}
      <ChannelBrowser
        open={flags.slackMessaging && channelBrowserOpen}
        onClose={() => setChannelBrowserOpen(false)}
        onJoinChannel={() => {
          setChannelBrowserOpen(false);
          useChatStore.getState().loadConversations();
        }}
      />

      {/* Create Conversation Dialog */}
      <CreateConversationDialog
        open={flags.slackMessaging && createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreate={handleCreateConversation}
      />
    </SidebarContainer>
  );
};

export default ChatSidebar;
